import React, { useCallback, useMemo, ReactElement } from 'react';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import IconTextBlock from '../../../../components/icon-text-block/icon-text-block.component';
import ActionButtonContainer from '../../../../components/action-button-container/action-button-container.component';
import DealValuationFileRowForm from '../deal-valuation-file-row-form/deal-valuation-file-row-form.component';
import EditIcon from '../../../../icons/edit-icon';
import MoreIcon from '../../../../icons/more-icon';
import DownloadIcon from '../../../../icons/download-icon';
import { DEAL_VALUATION_FILE_EDIT } from '../../../../constants/entity/permissions';
import { DATA_DEAL_VALUATION_FILE_ID } from '../../../../constants/system/data-attributes';

const DealValuationFileRow: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isNewDeal,
    isEditMode,
    dealValuationFileItem,
    dealValuationFileGroup,
    dealValuationFileFile,
    dealValuationFileActiveIds,
    fileDropDeps,
    actionMenuItems,
    onRowClick,
    onInlineSave,
    onInlineCancel,
    onFileChange,
    onFileDelete,
    onFileViewClick,
    onOpenActionMenu,
    onInlineEditModeButtonClick,
  } = props;

  const dealValuationFileEditPermissions: string[] = useMemo((): string[] => [DEAL_VALUATION_FILE_EDIT], []);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  return (
    (isEditMode || isNewDeal) && isActive ? (
      <DealValuationFileRowForm
        isActive={isActive}
        isInlineSaving={isInlineSaving}
        isSaving={isSaving}
        dealValuationFileData={dealValuationFileItem}
        dealValuationFileGroupData={dealValuationFileGroup}
        dealValuationFileFile={dealValuationFileFile}
        fileDropDeps={fileDropDeps}
        onFileChange={onFileChange}
        onFileDelete={onFileDelete}
        onInlineSave={onInlineSave}
        onInlineCancel={onInlineCancel}
        isSaveDisabled={isSaveDisabled}
      />
    ) : (
      <TableRow
        isButton
        onClick={(): void => {
          onRowClick(dealValuationFileItem, dealValuationFileGroup);
        }}
      >
        <TableCell width="50%">
          {dealValuationFileItem.file && dealValuationFileItem.file.name}
        </TableCell>
        {
          !isEditMode && (
            <TableCell>
              {
                dealValuationFileItem && dealValuationFileItem.file && (
                  `Date: ${dealValuationFileItem.file.date}`
                )
              }
            </TableCell>
          )
        }
        <TableCell width="5%">
          {
            !isEditMode && dealValuationFileItem.file && (
              <FilteredLink
                isExternal
                link={dealValuationFileItem.file.link}
                onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onFileViewClick(event, dealValuationFileItem.file, dealValuationFileGroup.name);
                }}
              >
                <IconTextBlock icon={<DownloadIcon />} />
              </FilteredLink>
            )
          }
        </TableCell>
        <TableCell isTextRight width="5%" onClick={onActionCellClick}>
          <ActionButtonContainer>
            {
              (!isEditMode || !dealValuationFileActiveIds
                || !dealValuationFileActiveIds.includes(dealValuationFileItem.id)) && (
                <CheckAccess permissions={dealValuationFileEditPermissions}>
                  <ActionButton
                    withSmallPadding
                    isHidden={!!isSaving}
                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      onInlineEditModeButtonClick(event, dealValuationFileItem.id);
                    }}
                  >
                    <EditIcon />
                  </ActionButton>
                </CheckAccess>
              )
            }
            {
              !isEditMode && (
                <CheckAccess
                  permissions={actionMenuItems
                    && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => (
                      actionMenuItem.permission
                    ))}
                >
                  <ActionButton
                    withPopup
                    withSmallPadding
                    isHidden={!!isSaving}
                    onClick={onOpenActionMenu}
                    {...{
                      [DATA_DEAL_VALUATION_FILE_ID]: dealValuationFileItem.id,
                    }}
                  >
                    <MoreIcon />
                  </ActionButton>
                </CheckAccess>
              )
            }
          </ActionButtonContainer>
        </TableCell>
      </TableRow>
    )
  );
});

export default DealValuationFileRow;
